import HeaderComponent from '@/components/HeaderRefactored/HeaderRefactored';
import { ActivityReports } from '@/resources/assets/images';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useRef, useState } from 'react';
import DatePicker, {
	staticRangesStandard
} from '@/components/DatePicker/DatePicker';
import { getUserEmail, getUserRole } from '@/services/authentication';
import { UserRole } from '@/modules/user/userTypes';
import { sendAnalytics } from '@/services/analyticsService';

import type { ReportUserPeriod } from '../@types/types';
import { Charts, ChartsHandle } from '../components/molecules/Charts';
import { ListSelector } from '../components/molecules/ListSelector';
import { BusinessesContactedSelector } from '../components/molecules/BusinessesContactedSelector';
import { EmailCampaigns } from '../components/molecules/EmailCampaigns';
import { ActivityFeed } from '../components/molecules/ActivityFeed';
import { useUserData } from '../hooks/useUserData';
import { MultiUsersActivityModal } from '../components/molecules/MultiUsersActivityModal';

export const ActivityReportsPage = () => {
	const chartsWrapperRef = useRef<ChartsHandle>(null);

	const [hasChartToExport, setHasChartToExport] = useState(false);

	const { user } = useUserData();

	const [reportUserPeriod, setReportUserPeriod] = useState<ReportUserPeriod>({
		startDate: startOfMonth(new Date()),
		endDate: endOfMonth(new Date()),
		userEmail: getUserRole() !== UserRole.User ? getUserEmail() : ''
	});

	const handleUpdateList = (listId: string) => {
		setReportUserPeriod(state => ({
			...state,
			listId
		}));
	};

	const handleUpdateMethod = (method: string | undefined) => {
		setReportUserPeriod(state => ({
			...state,
			method
		}));
	};

	const handleUpdateDate = ({
		startDate,
		endDate
	}: Pick<ReportUserPeriod, 'startDate' | 'endDate'>) => {
		setReportUserPeriod(state => ({
			...state,
			startDate,
			endDate
		}));

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `Filtered activity report by Date`,
			properties: {
				startDate,
				endDate
			}
		});
	};

	const handleUpdateUserEmail = (userEmail: string | string[]) => {
		setReportUserPeriod(state => ({
			...state,
			userEmail
		}));
	};

	const handleExportAsCsv = () => {
		chartsWrapperRef.current?.onExportAsCsv();
	};

	const handleExportAsPng = () => {
		chartsWrapperRef.current?.onExportAsPng();
	};

	const handleExportMultipleUsers = () => {
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User opened multiple user activity report'
		});
	};

	const handleUpdateChartsExistence = (value: boolean) => {
		setHasChartToExport(value);
	};

	return (
		<div className="flex h-full w-full flex-col gap-4 overflow-auto p-3">
			<HeaderComponent
				icon={<ActivityReports />}
				name="Activity Report"
				description="Track and analyze your business communication efforts. Stay updated with recent activities to optimize your outreach strategy."
			/>

			<header className="flex w-full flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
				<div className="flex w-full flex-col gap-3 md:w-fit md:flex-row md:items-center md:px-4">
					<p className="whitespace-nowrap text-dm-white">View by:</p>

					<ListSelector
						selectedList={reportUserPeriod.listId}
						onUpdateList={handleUpdateList}
					/>
				</div>

				<div className="flex w-full items-center gap-3 md:w-fit">
					<DatePicker
						minDate={undefined}
						maxDate={new Date()}
						reportUserPeriod={reportUserPeriod}
						onChange={handleUpdateDate}
						staticRanges={staticRangesStandard}
						wrapperClassName=""
					/>
				</div>
			</header>

			<section className="flex w-full flex-1 flex-col items-center justify-center gap-2 md:flex-row">
				<div className="flex h-full w-full flex-1 flex-col items-center justify-center gap-2 rounded-md">
					<header className="flex w-full items-center justify-between rounded-lg bg-dm-black p-4">
						<p className="text-lg font-semibold text-dm-white">
							Businesses Contacted
						</p>

						<BusinessesContactedSelector
							selectedMethod={reportUserPeriod.method}
							onUpdateMethods={handleUpdateMethod}
						/>
					</header>

					<section className="relative flex h-full min-h-[80vh] w-full items-center justify-center rounded-lg bg-dm-black p-4 md:min-h-0">
						<div className="absolute left-0 top-0 h-full w-full">
							<Charts
								reportUserPeriod={reportUserPeriod}
								ref={chartsWrapperRef}
								onUpdateChartsExistence={
									handleUpdateChartsExistence
								}
							/>
						</div>
					</section>
					{user.role !== UserRole.Free &&
						user?.role !== UserRole.PreTrial && (
							<footer className="flex w-full items-center gap-4 rounded-lg bg-dm-black p-4 font-semibold text-orange">
								<button
									type="button"
									disabled={!hasChartToExport}
									className="text-center text-sm leading-none transition-opacity disabled:cursor-not-allowed disabled:opacity-50 md:text-base [&:not(:disabled):hover]:underline"
									onClick={handleExportAsCsv}
								>
									Export as .csv
								</button>

								<button
									type="button"
									onClick={handleExportAsPng}
									disabled={!hasChartToExport}
									className="text-center text-sm leading-none transition-opacity disabled:cursor-not-allowed disabled:opacity-50 md:text-base [&:not(:disabled):hover]:underline"
								>
									Export as .png
								</button>

								{user.role !== 'User' && (
									<MultiUsersActivityModal
										reportUserPeriod={reportUserPeriod}
									>
										<button
											onClick={handleExportMultipleUsers}
											type="button"
											className="text-center text-sm leading-none transition-opacity disabled:cursor-not-allowed disabled:opacity-50 md:text-base [&:not(:disabled):hover]:underline"
										>
											Export for multiple users
										</button>
									</MultiUsersActivityModal>
								)}
							</footer>
						)}
				</div>

				<div className="flex h-full w-full max-w-xs flex-col items-center justify-center gap-2 rounded-md md:flex-1 xl:max-w-sm">
					<section className="flex w-full flex-col justify-between gap-5 rounded-lg bg-dm-black p-4">
						<p className="text-lg font-semibold text-dm-white">
							Email Campaigns
						</p>

						<hr className="w-full rounded-full border border-dark" />

						<EmailCampaigns
							selectedEmail={
								typeof reportUserPeriod.userEmail === 'object'
									? reportUserPeriod.userEmail[0]
									: reportUserPeriod.userEmail
							}
							onUpdateUserEmail={handleUpdateUserEmail}
						/>
					</section>

					<section className="flex h-full w-full flex-col gap-6 rounded-lg bg-dm-black p-4">
						<p className="text-lg font-semibold text-dm-white">
							Activity Feed
						</p>

						<hr className="w-full rounded-full border border-dark" />

						<ActivityFeed />
					</section>
				</div>
			</section>
		</div>
	);
};
